import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { buildWhatsAppChatHref } from '@/lib/whatsapp-link'
import formStyles from '../CampanhaEditPage.module.css'
import styles from './CampaignWhatsAppLinkField.module.css'

function fieldsFromLink(value: string): { phone: string; message: string } {
  try {
    const url = new URL(value)
    if (url.hostname === 'wa.me') {
      return {
        phone: url.pathname.replace(/^\//, ''),
        message: url.searchParams.get('text') ?? '',
      }
    }
    if (url.hostname === 'api.whatsapp.com' && url.pathname.startsWith('/send')) {
      return {
        phone: url.searchParams.get('phone') ?? '',
        message: url.searchParams.get('text') ?? '',
      }
    }
  } catch {
    // Links antigos fora do formato wa.me podem ser substituídos no assistente.
  }
  return { phone: '', message: '' }
}

function WhatsAppLinkHelpModal({
  initialLink,
  onApply,
  onClose,
}: {
  initialLink: string
  onApply: (link: string) => void
  onClose: () => void
}) {
  const initialFields = fieldsFromLink(initialLink)
  const [phone, setPhone] = useState(initialFields.phone)
  const [message, setMessage] = useState(initialFields.message)
  const [applied, setApplied] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  const link = buildWhatsAppChatHref(phone, message)
  const linkTooLong = Boolean(link && link.length > 512)
  const validLink = linkTooLong ? null : link

  useEffect(() => {
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onEscape)
    return () => document.removeEventListener('keydown', onEscape)
  }, [onClose])

  const handleTabKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'input, textarea, button:not(:disabled)'
    )
    if (!focusable?.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const generateLink = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (!validLink) return
    onApply(validLink)
    setApplied(true)
  }

  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="whatsapp-help-title"
        aria-describedby="whatsapp-help-description"
        onKeyDown={handleTabKey}
      >
        <div className={styles.header}>
          <span className={styles.headerIcon} aria-hidden>
            <Icon icon="mdi:whatsapp" />
          </span>
          <div>
            <h2 id="whatsapp-help-title">Criar link do WhatsApp</h2>
            <p id="whatsapp-help-description">
              Informe o número e uma mensagem para iniciar a conversa.
            </p>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Fechar ajuda"
          >
            <Icon icon="mdi:close" aria-hidden />
          </button>
        </div>
        <form onSubmit={generateLink} className={styles.form}>
          <label className={styles.label} htmlFor="whatsapp-help-phone">
            Número de WhatsApp
          </label>
          <input
            id="whatsapp-help-phone"
            className={formStyles.editInput}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            autoFocus
            placeholder="Ex.: (48) 99988-7766"
            value={phone}
            onChange={(event) => {
              setPhone(event.target.value)
              setApplied(false)
            }}
          />
          <p className={styles.hint}>
            Inclua o DDD. O código 55 do Brasil será adicionado quando necessário.
          </p>
          {phone.trim() && !link && (
            <p className={styles.error} role="alert">
              Informe um número com DDD válido.
            </p>
          )}
          <label className={styles.label} htmlFor="whatsapp-help-message">
            Mensagem inicial <span>(opcional)</span>
          </label>
          <textarea
            id="whatsapp-help-message"
            className={formStyles.editTextarea}
            rows={3}
            maxLength={500}
            placeholder="Ex.: Olá! Quero saber mais sobre esta campanha."
            value={message}
            onChange={(event) => {
              setMessage(event.target.value)
              setApplied(false)
            }}
          />
          {linkTooLong && (
            <p className={styles.error} role="alert">
              A mensagem é longa demais para o link da campanha. Encurte o texto para
              continuar.
            </p>
          )}
          {validLink && (
            <p className={styles.preview}>
              Prévia: <span>{validLink}</span>
            </p>
          )}
          {applied && (
            <p className={styles.success} role="status">
              Link aplicado ao campo. Salve a campanha para publicar.
            </p>
          )}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.testButton}
              disabled={!validLink}
              onClick={() => {
                if (validLink) window.open(validLink, '_blank', 'noopener,noreferrer')
              }}
            >
              <Icon icon="mdi:open-in-new" aria-hidden />
              Testar
            </button>
            <button type="submit" className={styles.generateButton} disabled={!validLink}>
              <Icon icon="mdi:link-variant" aria-hidden />
              Gerar link
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

export function CampaignWhatsAppLinkField({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const [helpOpen, setHelpOpen] = useState(false)
  const helpButtonRef = useRef<HTMLButtonElement>(null)
  const closeHelp = () => {
    setHelpOpen(false)
    window.setTimeout(() => helpButtonRef.current?.focus(), 0)
  }

  return (
    <div className={formStyles.editField}>
      <label className={formStyles.editLabel} htmlFor="campaign-whatsapp-public">
        WhatsApp da campanha
        <span className={formStyles.editLabelHint}>
          (Mesmo link no botão flutuante e no botão Contato da página)
        </span>
      </label>
      <div className={styles.fieldRow}>
        <input
          id="campaign-whatsapp-public"
          type="url"
          maxLength={512}
          className={formStyles.editInput}
          placeholder="Ex.: https://wa.me/5548999887766"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          ref={helpButtonRef}
          type="button"
          className={styles.helpButton}
          onClick={() => setHelpOpen(true)}
        >
          <Icon icon="mdi:help-circle-outline" aria-hidden />
          Preciso de Ajuda
        </button>
      </div>
      {helpOpen && (
        <WhatsAppLinkHelpModal
          initialLink={value}
          onApply={onChange}
          onClose={closeHelp}
        />
      )}
    </div>
  )
}
